import { KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Alert, InputDate } from 'medere-ui-framework';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dropdown } from 'primereact/dropdown';
import { ProgressSpinner } from 'primereact/progressspinner';
import { classNames } from 'primereact/utils';
import {
    HealthEntityNomenclatorDTO,
    HealthEntityNomenclatorOption,
    HealthEntityNomenclatorOptionsDTO,
    HealthEntityNomenclatorRequestDTO,
} from '../../dto/HealthEntityNomenclatorDTO';
import {
    createHealthEntityNomenclator,
    deleteHealthEntityNomenclator,
    getHealthEntityNomenclatorOptions,
    getHealthEntityNomenclators,
    updateHealthEntityNomenclator,
} from '../../services/HealthEntityNomenclatorController';
import { parseApiError } from '../../services/apiError';
import { toISOString } from '../../utils/localeHelpers';
import useNotify from '../../hooks/useNotify';
import LoadTable, { LoadTableColumn, LoadTableRow } from '../../components/novarium/LoadTable';
import NvTooltips from '../../components/novarium/NvTooltips';
import LocationActionMenu from '../Location/tabs/components/LocationActionMenu';
import './HealthEntityNomenclator.scss';

type FieldName = 'healthEntityId' | 'nomenclatorSourceId' | 'validFrom' | 'validTo';
type FieldErrors = Partial<Record<FieldName, string>>;
const FIELDS: FieldName[] = ['healthEntityId', 'nomenclatorSourceId', 'validFrom', 'validTo'];
const isField = (field: string): field is FieldName => (FIELDS as string[]).includes(field);

const FIELD_LABELS: Record<FieldName, string> = {
    healthEntityId: 'Obra social',
    nomenclatorSourceId: 'Fuente de nomenclador',
    validFrom: 'Desde',
    validTo: 'Hasta',
};
const OVERLAPPING_VALIDITY = 'OVERLAPPING_VALIDITY';
const ERROR_IDS: Record<FieldName, string> = {
    healthEntityId: 'health-entity-nomenclator-health-entity-error',
    nomenclatorSourceId: 'health-entity-nomenclator-source-error',
    validFrom: 'health-entity-nomenclator-valid-from-error',
    validTo: 'health-entity-nomenclator-valid-to-error',
};

const EMPTY_OPTIONS: HealthEntityNomenclatorOptionsDTO = { healthEntities: [], nomenclatorSources: [] };
const INACTIVE_SUFFIX = ' (inactiva)';

type Row = LoadTableRow & { item: HealthEntityNomenclatorDTO };
const rowKeyOf = (item: HealthEntityNomenclatorDTO) => `hen-${item.id}`;

function today(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

function toIsoDate(date: Date | null): string | null {
    return date ? toISOString(date)!.substring(0, 10) : null;
}

function fromIsoDate(value: string | null): Date | null {
    const match = value ? /^(\d{4})-(\d{2})-(\d{2})/.exec(value) : null;
    return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
}

function formatDate(value: string | null): string {
    const match = value ? /^(\d{4})-(\d{2})-(\d{2})/.exec(value) : null;
    return match ? `${match[3]}/${match[2]}/${match[1]}` : '—';
}

function healthEntityLabel(item: HealthEntityNomenclatorDTO): string {
    return item.healthEntityActive ? item.healthEntityName : `${item.healthEntityName}${INACTIVE_SUFFIX}`;
}


export default function HealthEntityNomenclatorView() {
    const notify = useNotify();

    const [items, setItems] = useState<HealthEntityNomenclatorDTO[]>([]);
    const [options, setOptions] = useState<HealthEntityNomenclatorOptionsDTO>(EMPTY_OPTIONS);
    const [loading, setLoading] = useState(true);
    const [healthEntityId, setHealthEntityId] = useState<number | null>(null);
    const [nomenclatorSourceId, setNomenclatorSourceId] = useState<number | null>(null);
    const [validFrom, setValidFrom] = useState<Date | null>(today());
    const [validTo, setValidTo] = useState<Date | null>(null);
    const [editing, setEditing] = useState<HealthEntityNomenclatorDTO | null>(null);
    const [errors, setErrors] = useState<FieldErrors>({});
    const [conflict, setConflict] = useState<string | undefined>();
    const [saving, setSaving] = useState(false);
    const healthEntityRef = useRef<Dropdown>(null);
    const sourceRef = useRef<Dropdown>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [loadedItems, loadedOptions] = await Promise.all([
                getHealthEntityNomenclators(),
                getHealthEntityNomenclatorOptions(),
            ]);
            setItems(loadedItems);
            setOptions(loadedOptions);
        } catch {
            notify.error('No se pudieron cargar los nomencladores reconocidos. Intentá de nuevo.');
        } finally {
            setLoading(false);
        }
    }, [notify]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => { setConflict(undefined); }, [healthEntityId, nomenclatorSourceId, validFrom, validTo]);

    const healthEntityOptions: HealthEntityNomenclatorOption[] = editing
        && !options.healthEntities.some(option => option.id === editing.healthEntityId)
        ? [{ id: editing.healthEntityId, name: healthEntityLabel(editing) }, ...options.healthEntities]
        : options.healthEntities;

    function clearError(field: FieldName) {
        setErrors(current => ({ ...current, [field]: undefined }));
    }

    function resetForm() {
        setHealthEntityId(null);
        setNomenclatorSourceId(null);
        setValidFrom(today());
        setValidTo(null);
        setEditing(null);
        setErrors({});
        setConflict(undefined);
    }

    function startEdit(item: HealthEntityNomenclatorDTO) {
        setHealthEntityId(item.healthEntityId);
        setNomenclatorSourceId(item.nomenclatorSourceId);
        setValidFrom(fromIsoDate(item.validFrom));
        setValidTo(fromIsoDate(item.validTo));
        setEditing(item);
        setErrors({});
        setConflict(undefined);
        healthEntityRef.current?.focus();
    }

    function cancelEdit() {
        resetForm();
        healthEntityRef.current?.focus();
    }

    function validate(): FieldErrors {
        const result: FieldErrors = {};
        if (healthEntityId == null) result.healthEntityId = 'Elegí la obra social';
        if (nomenclatorSourceId == null) result.nomenclatorSourceId = 'Elegí la fuente de nomenclador';
        if (!validFrom) result.validFrom = 'Completá la fecha desde';
        else if (validTo && validTo < validFrom) result.validTo = 'Ingresá una fecha igual o posterior a la fecha desde';
        return result;
    }

    function focusFirstError(fieldErrors: FieldErrors) {
        if (fieldErrors.healthEntityId) healthEntityRef.current?.focus();
        else if (fieldErrors.nomenclatorSourceId) sourceRef.current?.focus();
    }

    async function handleSubmit() {
        if (saving) return;
        const validationErrors = validate();
        setErrors(validationErrors);
        setConflict(undefined);
        if (Object.keys(validationErrors).length > 0) {
            focusFirstError(validationErrors);
            return;
        }

        const body: HealthEntityNomenclatorRequestDTO = {
            healthEntityId,
            nomenclatorSourceId,
            validFrom: toIsoDate(validFrom),
            validTo: toIsoDate(validTo),
        };
        setSaving(true);
        try {
            if (editing) {
                const updated = await updateHealthEntityNomenclator(editing.id, body);
                setItems(current => current.map(item => (item.id === updated.id ? updated : item)));
                notify.success('Cambios guardados');
            } else {
                const created = await createHealthEntityNomenclator(body);
                // La fila nueva va primera
                setItems(current => [created, ...current]);
                notify.success('Nomenclador reconocido agregado');
            }
            resetForm();
            healthEntityRef.current?.focus();
        } catch (error: unknown) {
            handleSaveError(error);
        } finally {
            setSaving(false);
        }
    }

    function handleSaveError(error: unknown) {
        const apiError = parseApiError(error);
        // El solape no es de un campo (es la combinación de obra social, fuente y fechas): va en el Alert, sin marcar
        if (apiError.errorCode === OVERLAPPING_VALIDITY) {
            setConflict(apiError.message);
            return;
        }
        const fieldErrors: FieldErrors = {};
        for (const detail of apiError.details ?? []) {
            if (isField(detail.field)) fieldErrors[detail.field] = detail.message;
        }
        if (Object.keys(fieldErrors).length > 0) {
            setErrors(fieldErrors);
            focusFirstError(fieldErrors);
            return;
        }
        if (apiError.status === 404) {
            notify.error(apiError.message);
            resetForm();
            load();
            return;
        }
        const isBusinessError = apiError.status >= 400 && apiError.status < 500;
        const fallback = editing
            ? 'No se pudieron guardar los cambios. Intentá de nuevo.'
            : 'No se pudo agregar el nomenclador reconocido. Intentá de nuevo.';
        notify.error(isBusinessError ? apiError.message : fallback);
    }

    function askDelete(item: HealthEntityNomenclatorDTO) {
        const period = item.validTo
            ? `entre el ${formatDate(item.validFrom)} y el ${formatDate(item.validTo)}`
            : `desde el ${formatDate(item.validFrom)}`;
        confirmDialog({
            header: '¿Eliminar nomenclador reconocido?',
            message: `${item.healthEntityName} deja de reconocer ${item.nomenclatorSourceName} ${period}, también `
                + 'para las prácticas de esas fechas que ya se cargaron.',
            className: 'nv-scope nv-confirm',
            style: { width: '420px' },
            breakpoints: { '640px': '100vw' },
            footer: (options) => (
                <>
                    <Button type="button" className="buttonMain2" outlined label="Cancelar" onClick={options.reject}
                        autoFocus />
                    <Button type="button" className="buttonMain" label="Eliminar" onClick={options.accept} />
                </>
            ),
            accept: () => { handleDelete(item); },
        });
    }

    async function handleDelete(item: HealthEntityNomenclatorDTO) {
        try {
            await deleteHealthEntityNomenclator(item.id);
            setItems(current => current.filter(i => i.id !== item.id));
            if (editing?.id === item.id) resetForm();
            notify.success('Nomenclador reconocido eliminado');
        } catch (error: unknown) {
            const apiError = parseApiError(error);
            const isBusinessError = apiError.status >= 400 && apiError.status < 500;
            notify.error(isBusinessError
                ? apiError.message
                : 'No se pudo eliminar el nomenclador reconocido. Intentá de nuevo.');
            if (apiError.status === 404) load();
        }
    }

    function handleFormKeyDown(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleSubmit();
        } else if (event.key === 'Escape' && editing) {
            event.preventDefault();
            cancelEdit();
        }
    }

    const columns: LoadTableColumn<Row>[] = [
        {
            key: 'healthEntity',
            header: 'Obra social',
            className: 'health-entity-nomenclator__health-entity-col',
            addCell: (
                <div className="nv-cell-field" onKeyDown={handleFormKeyDown}>
                    <Dropdown
                        ref={healthEntityRef}
                        inputId="health-entity-nomenclator-health-entity"
                        value={healthEntityId}
                        options={healthEntityOptions}
                        optionLabel="name"
                        optionValue="id"
                        filter
                        placeholder="Elegí la obra social"
                        emptyFilterMessage="No hay obras sociales con ese nombre"
                        panelClassName="health-entity-nomenclator__health-entity-panel"
                        onChange={(e) => {
                            setHealthEntityId(e.value ?? null);
                            clearError('healthEntityId');
                        }}
                        className={classNames('nv-in-cell health-entity-nomenclator__health-entity',
                            { 'p-invalid': errors.healthEntityId })}
                        aria-label="Obra social"
                        aria-invalid={errors.healthEntityId ? true : undefined}
                        aria-describedby={errors.healthEntityId ? ERROR_IDS.healthEntityId : undefined}
                    />
                </div>
            ),
            body: ({ item }) => (
                <span className="nv-truncate nv-tip" data-pr-tooltip={healthEntityLabel(item)}>
                    {healthEntityLabel(item)}
                </span>
            ),
        },
        {
            key: 'nomenclatorSource',
            header: 'Fuente de nomenclador',
            className: 'health-entity-nomenclator__source',
            addCell: (
                <div className="nv-cell-field" onKeyDown={handleFormKeyDown}>
                    <Dropdown
                        ref={sourceRef}
                        inputId="health-entity-nomenclator-source"
                        value={nomenclatorSourceId}
                        options={options.nomenclatorSources}
                        optionLabel="name"
                        optionValue="id"
                        placeholder="Elegí la fuente"
                        onChange={(e) => {
                            setNomenclatorSourceId(e.value ?? null);
                            clearError('nomenclatorSourceId');
                        }}
                        className={classNames('nv-in-cell', { 'p-invalid': errors.nomenclatorSourceId })}
                        aria-label="Fuente de nomenclador"
                        aria-invalid={errors.nomenclatorSourceId ? true : undefined}
                        aria-describedby={errors.nomenclatorSourceId ? ERROR_IDS.nomenclatorSourceId : undefined}
                    />
                </div>
            ),
            body: ({ item }) => item.nomenclatorSourceName,
        },
        {
            key: 'validFrom',
            header: 'Desde',
            align: 'center',
            className: 'nv-col-date health-entity-nomenclator__date',
            addCell: (
                <div className="nv-cell-field" onKeyDown={handleFormKeyDown}>
                    <InputDate
                        value={validFrom}
                        onChange={(e: { value?: Date | null }) => {
                            setValidFrom(e.value ?? null);
                            setErrors(current => ({ ...current, validFrom: undefined, validTo: undefined }));
                        }}
                        placeholder="Desde"
                        error={Boolean(errors.validFrom)}
                    />
                </div>
            ),
            body: ({ item }) => formatDate(item.validFrom),
        },
        {
            key: 'validTo',
            header: 'Hasta',
            align: 'center',
            className: 'nv-col-date health-entity-nomenclator__date',
            addCell: (
                <div className="nv-cell-field" onKeyDown={handleFormKeyDown}>
                    <InputDate
                        value={validTo}
                        onChange={(e: { value?: Date | null }) => {
                            setValidTo(e.value ?? null);
                            clearError('validTo');
                        }}
                        placeholder="Sin fin"
                        error={Boolean(errors.validTo)}
                    />
                </div>
            ),
            body: ({ item }) => (item.validTo ? formatDate(item.validTo) : 'Sin fin'),
        },
    ];

    const addAction = editing ? (
        <span className="nv-cell-actions">
            <Button
                type="button"
                text
                className="nv-icon nv-tip"
                icon="pi pi-check"
                aria-label="Confirmar cambios"
                data-pr-tooltip="Confirmar cambios"
                loading={saving}
                onClick={handleSubmit}
            />
            <Button
                type="button"
                text
                className="nv-icon nv-tip"
                icon="pi pi-times"
                aria-label="Cancelar cambios"
                data-pr-tooltip="Cancelar cambios"
                disabled={saving}
                onClick={cancelEdit}
            />
        </span>
    ) : (
        <Button
            type="button"
            icon="pi pi-plus-circle text-3xl"
            className="buttonSecondary2 p-button-rounded nv-add-row-btn nv-tip"
            aria-label="Agregar"
            data-pr-tooltip="Agregar"
            loading={saving}
            onClick={handleSubmit}
        />
    );

    const rows: Row[] = items.map(item => ({ rowKey: rowKeyOf(item), item }));
    const fieldsWithError = FIELDS.filter(field => errors[field]);

    return (
        <div className="nv-scope nv-page health-entity-nomenclator">
            <NvTooltips />
            <ConfirmDialog />
            <div className="nv-page-head">
                <h1 className="nv-h1">Nomencladores reconocidos por obra social</h1>
            </div>

            {loading ? (
                <div className="nv-loading" role="status">
                    <ProgressSpinner strokeWidth="4" style={{ width: 32, height: 32 }} />
                    <span>Cargando…</span>
                </div>
            ) : (
                <div className="health-entity-nomenclator__body">
                    <LoadTable
                        rows={rows}
                        columns={columns}
                        addAction={addAction}
                        rowActions={({ item }) => (
                            <LocationActionMenu
                                items={[
                                    { label: 'Editar', icon: 'pi pi-pencil', action: () => startEdit(item) },
                                    { label: 'Eliminar', icon: 'pi pi-trash', action: () => askDelete(item) },
                                ]}
                                ariaLabel={`Opciones de ${item.nomenclatorSourceName} de ${item.healthEntityName}`}
                                buttonId={`healthEntityNomenclatorMenu_${item.id}`}
                                panelClassName="health-entity-nomenclator-action-menu-panel"
                                buttonClassName="p-button-text nv-icon"
                                buttonDisabled={saving}
                            />
                        )}
                        emptyMessage="Todavía no hay nomencladores reconocidos cargados."
                        editingRowKey={editing ? rowKeyOf(editing) : null}
                    />
                    <div className="health-entity-nomenclator__alert" aria-live="polite">
                        {(conflict || fieldsWithError.length > 0) && (
                            <Alert
                                color="yellow"
                                icon="pi-exclamation-triangle"
                                className="py-2 px-3"
                                contentClassName="flex align-items-start"
                            >
                                <div className="health-entity-nomenclator__alert-text text-yellow-700 font-medium">
                                    {conflict && <span>{conflict}</span>}
                                    {fieldsWithError.map(field => (
                                        <span key={field} id={ERROR_IDS[field]}>
                                            {FIELD_LABELS[field]}: {errors[field]}
                                        </span>
                                    ))}
                                </div>
                            </Alert>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
